"use client";

import { useAppContext } from "../appContext";
import { AnimatePresence, m } from "framer-motion";
import TransitionLink from "../transitionLink";
import MarketingSvg from "../svg/marketing";
import { dividerVariants } from "../variants";
import LanguageSwitch from "./languageSwitch";
import { ServiceMenuItem } from "./serviceMenuItem";
import { useEffect, useRef } from "react";
import CalendlyButton from "../calendlyButton";
import Button from "../button";

interface HeaderProps {
  data?: any;
  translations?: { en: string; cs: string };
}

const menuVariants = {
  initial: {
    x: "100%",
  },
  animate: {
    x: 0,
    transition: {
      duration: 0.5,
      staggerChildren: 0.05,
      delayChildren: 0.2,
      ease: [0.22, 1, 0.36, 1],
    },
  },
  exit: {
    x: "100%",
    transition: {
      duration: 0.5,
      ease: [0.22, 1, 0.36, 1],
    },
  },
};

const menuItemVariants = {
  initial: {
    x: 64,
    opacity: 0,
  },
  animate: {
    x: 0,
    opacity: 1,
    transition: {
      duration: 0.3,
      ease: [0.22, 1, 0.36, 1],
    },
  },
};

function MenuItem({ item }: any) {
  const { toggleMenu } = useAppContext();
  return (
    <>
      <m.div
        variants={dividerVariants}
        className={`w-full h-px bg-black origin-right`}
      />
      <m.div
        variants={menuItemVariants}
        className={`w-full relative overflow-hidden flex justify-center h-[12vw] lg:h-28 flex items-center justify-center`}
      >
        {item.type === "file" ? (
          <a
            href={item.file.asset.url}
            target="_blank"
            //download={item.file.asset.originalFilename}
            className="text-center font-display h1 uppercase w-full pb-[1vw] lg:pb-3 px-4"
          >
            {item.title}
          </a>
        ) : (
          <TransitionLink
            onPointerUp={() => toggleMenu(null)}
            href={`/${item.page?.slug?.current}`}
            classes="text-center font-display h1 uppercase w-full pb-[1vw] lg:pb-3 px-4"
          >
            {item.title}
          </TransitionLink>
        )}
      </m.div>
    </>
  );
}

function HeaderMobile({ data, translations }: HeaderProps) {
  const { menu, toggleMenu, openLightbox, trackCalendlyClick } = useAppContext();
  const menuRef = useRef<HTMLDivElement>(null);

  // Escape key to close mobile menu
  useEffect(() => {
    if (menu !== "mobile") return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") toggleMenu(null);
    };
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, [menu, toggleMenu]);

  return (
    <>
      <div
        className={`2xl:hidden relative z-[1000001] w-full bg-white transition-all ${menu ? "" : "shadow-lg shadow-neutral-800/5"}`}
      >
        <div className="container mx-auto px-4 flex justify-between items-center py-4">
          <TransitionLink
            href="/"
            classes="logo-link"
            onPointerUp={() => toggleMenu(null)}
            title="Navigate to homepage"
          >
            <MarketingSvg />
          </TransitionLink>

          {data?.cta && (
            <button
              className="btn flex flex-col items-center justify-center gap-1 min-w-[44px] min-h-[44px]"
              onClick={() => toggleMenu("mobile")}
              aria-label={menu === "mobile" ? "Close menu" : "Open menu"}
              aria-expanded={menu === "mobile"}
            >
              <span
                className={`${menu === "mobile" ? "rotate-[135deg] " : "translate-y-[-.25rem]"} w-4 h-px bg-white block transition-all duration-200 absolute`}
              />
              <span
                className={`${menu === "mobile" ? "scale-x-0 opacity-0" : ""} w-4 h-px bg-white block transition-all duration-200 absolute`}
              />
              <span
                className={`${menu === "mobile" ? "rotate-[-135deg] " : "translate-y-[.25rem]"} w-4 h-px bg-white block transition-all duration-200 absolute`}
              />
            </button>
          )}
        </div>
      </div>
      <AnimatePresence key={`menu-mobile`}>
        {menu === "mobile" && (
          <m.div
            ref={menuRef}
            role="dialog"
            aria-modal="true"
            aria-label="Navigation menu"
            variants={menuVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            className="w-screen h-dvh overflow-y-scroll left-0 top-0 flex 2xl:hidden flex-col items-center bg-white fixed shadow-lg shadow-neutral-800/5"
          >
            <nav className="flex flex-col grow mt-20 items-center justify-center w-full">
              {data?.menu &&
                data.menu.map((item: any, index: number) => {
                  if (item.type === "page") {
                    return (
                      <m.div
                        key={`item-${index}`}
                        variants={menuItemVariants}
                        className="w-full"
                      >
                        <ServiceMenuItem subItem={item} />
                      </m.div>
                    );
                  } else if (item.type === "file") {
                    return <MenuItem key={`item-${index}`} item={item} />;
                  } else {
                    return (
                      <div
                        key={index}
                        className="w-full flex flex-col items-center justify-center"
                      >
                        <div className="w-full">
                          {item.menuItems?.map((subItem: any, i: number) => (
                            <m.div
                              key={`subItem-${i}`}
                              variants={menuItemVariants}
                              className="w-full"
                            >
                              <ServiceMenuItem subItem={subItem} />
                            </m.div>
                          ))}
                        </div>
                      </div>
                    );
                  }
                })}
              <m.div
                variants={dividerVariants}
                className={`w-full h-px bg-black origin-right`}
              />
              <m.div
                variants={menuItemVariants}
                className="flex gap-4 w-full p-4 justify-center"
              >
                {data?.cta && (
                  <Button
                    type="button"
                    className="grow min-w-0 justify-center [&>span:last-child]:left-1/2 [&>span:last-child]:-translate-x-1/2 [&>span:last-child]:whitespace-nowrap"
                    onClick={() => openLightbox("cta")}
                  >
                    {data.cta}
                  </Button>
                )}
                {data?.bookCallCta?.link && (
                  <div className="grow flex">
                    <CalendlyButton
                      url={data.bookCallCta.link}
                      variant="red"
                      className="grow min-w-0 justify-center [&>span:last-child]:left-1/2 [&>span:last-child]:-translate-x-1/2 [&>span:last-child]:whitespace-nowrap"
                      onOpen={trackCalendlyClick}
                    >
                      {data.bookCallCta.title}
                    </CalendlyButton>
                  </div>
                )}
              </m.div>
            </nav>

            <div className="flex items-center justify-between w-full p-4 bg-neutral-100 border-t border-neutral-200">
              <LanguageSwitch translations={translations} mobile />

              <div className="flex items-center justify-end">
                <a
                  className="hover:opacity-60 transition-opacity mono px-2 py-3"
                  href="https://tmrw.film/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Tmrw.film
                </a>
                <a
                  className="hover:opacity-60 transition-opacity mono px-2 py-3"
                  href="https://tmrw.pictures/"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Tmrw.pics
                </a>
              </div>
            </div>
          </m.div>
        )}
      </AnimatePresence>
    </>
  );
}

export default HeaderMobile;
